import Bugsnag from '@bugsnag/js';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
  IconButton,
  SkeletonSpinnerCentered,
  Tooltip,
} from '@civilgrid/ui/components';
import { centroid } from '@turf/turf';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getZoningData as apiGetZoningData } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import store from '~/pages/projectDetails/landRights/store';
import rootStore from '~/store';
import ZoningTable from './ZoningTable';
import type { PropertyZoningDetails } from './zoneTypes';

interface ZoningDialogProps {
  zoningDialogOpen: boolean;
  setZoningDialogOpen: (value: boolean) => void;
  zoningDataAccess?: boolean;
  selectedIds: string[];
  project: Project | undefined;
}

export default function ZoningDialog({
  zoningDialogOpen,
  setZoningDialogOpen,
  zoningDataAccess,
  selectedIds,
  project,
}: ZoningDialogProps) {
  const [zoningForParcel, setZoningForParcel] = useState<PropertyZoningDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Blocks a second click that lands before the loading state re-renders the button as disabled.
  const requestInFlight = useRef(false);

  const handleZoningClick = async () => {
    if (requestInFlight.current) return;
    if (zoningDataAccess && selectedIds.length === 1) {
      requestInFlight.current = true;
      setIsLoading(true);
      const selectedId = Array.from(selectedIds)[0];
      try {
        const selectedParcel = Reflect.get(store, 'parcelDetails').find((parcel) => parcel.parcelId === selectedId);
        if (!selectedParcel || !project) return;
        const parcelCentroid = centroid(selectedParcel.geom);
        const zoningDataResponse = await apiGetZoningData(
          project.id,
          selectedParcel.parcelId,
          parcelCentroid.geometry.coordinates,
        );

        setZoningForParcel(zoningDataResponse);
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        // A 401 or 403 is an expired session or denied access, which the api client interceptor already handles.
        if (status !== 401 && status !== 403) {
          Bugsnag.notify(new Error('Zoning lookup failed', { cause: error }), (event) => {
            event.context = 'zoning:lookup';
            event.addMetadata('zoning', { projectId: project?.id, parcelId: selectedId, status });
          });
        }
        Notify.error({
          title: 'Zoning Data Error',
          message: 'There was an error while loading the zoning data. Please contact support.',
        });
        setZoningDialogOpen(false);
      } finally {
        requestInFlight.current = false;
        setIsLoading(false);
      }
    }
  };

  const handleExportClick = () => {
    const zoningTable = document.getElementById('zoning-table');
    if (zoningTable) {
      zoningTable.dispatchEvent(new CustomEvent('exportCsv'));
      if (project) {
        capturePosthogEvent(POSTHOG_EVENTS.zoningDataDownloaded, {
          project_id: project.id,
          organization_id: rootStore.currentOrganizationId || undefined,
        });
      }
    }
  };

  const unauthorized = (
    <div className="text-sm font-noto">
      Your account does not have access to Zoning Data. Please contact{' '}
      <a
        href="mailto:support@civilgrid.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-50 underline hover:text-blue-60"
      >
        Support
      </a>{' '}
      to request access.
    </div>
  );

  const dialogTitle = (
    <span className="flex space-x-2">
      Zoning Data (powered by&nbsp;
      <a
        href="https://www.zoneomics.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-50 underline hover:text-blue-60"
      >
        Zoneomics
      </a>
      )
      {zoningDataAccess && (
        <Tooltip
          asChild
          text="Download as CSV"
          side="top"
          sideOffset={5}
          delayDuration={500}
          className="!z-[200]" // TOOD: fix tooltip z-index, [data-radix-popper-content-wrapper], stays at z-index: 10 !important; and is below the modal.
        >
          <IconButton
            icon="download"
            variant="hollow"
            aria-label="Download as CSV"
            data-testid="zoning-export-csv-button"
            onClick={() => {
              handleExportClick();
            }}
          />
        </Tooltip>
      )}
    </span>
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const zoningDisplay = useMemo(() => {
    if (zoningForParcel) {
      return <ZoningTable apn={selectedIds[0]} zoningForParcel={zoningForParcel.data} />;
    }

    return <SkeletonSpinnerCentered />;
  }, [zoningForParcel]);

  useEffect(() => {
    if (!zoningDialogOpen) {
      setZoningForParcel(null);
    }
  }, [zoningDialogOpen]);

  return (
    <Dialog open={zoningDialogOpen} onOpenChange={setZoningDialogOpen}>
      <DialogTrigger asChild>
        <IconButton
          onClick={handleZoningClick}
          disabled={selectedIds.length !== 1}
          loading={isLoading}
          icon="component2"
          data-testid="get-zoning-info-button"
        >
          Get Zoning Info
        </IconButton>
      </DialogTrigger>
      <DialogContent
        title={dialogTitle}
        className="z-[110] w-auto min-w-min max-w-[min(75vw,80rem)] w-11/12 h-5/6"
        overlayClassName="z-[100]"
        data-testid="get-zoning-info-dialog"
      >
        <DialogBody className="h-[95%] overflow-y-auto">{zoningDataAccess ? zoningDisplay : unauthorized}</DialogBody>
      </DialogContent>
    </Dialog>
  );
}
